import { type Infer } from "spacetimedb";

import type CampaignBrowseNominationsRow from "@tycho/web-bindings/campaign_browse_nominations_table";
import type CampaignBrowseShareableCardsRow from "@tycho/web-bindings/campaign_browse_shareable_cards_table";
import type MyBusinessCardInterestsRow from "@tycho/web-bindings/my_business_card_interests_table";
import type MyNominationInterestsRow from "@tycho/web-bindings/my_nomination_interests_table";
import type { CanonicalAddress } from "@tycho/web-bindings/types";

import {
  type BusinessCategoryType,
  getFriendlyName,
} from "@/web/lib/schemaUtils/businessCategories";
import type {
  BrowseResult,
  BusinessCardUserInterest,
  CampaignColors,
  NominatedBrowseResult,
  NominatedBusiness,
  OfferingBrowseResult,
  SharableBusinessCard,
  SharableBusinessCardBrowseResult,
} from "@/web/routes/browse/-components/types";

type CampaignBrowseNomination = Infer<typeof CampaignBrowseNominationsRow>;
type CampaignBrowseShareableCard = Infer<typeof CampaignBrowseShareableCardsRow>;
type MyBusinessCardInterest = Infer<typeof MyBusinessCardInterestsRow>;
type MyNominationInterest = Infer<typeof MyNominationInterestsRow>;
function toNumber(value: number | bigint | undefined): number {
  if (value === undefined) return 0;
  return typeof value === "bigint" ? Number(value) : value;
}

/** Spacetime `Timestamp` → ms since epoch for the deterministic "recent" sort. */
function timestampToMs(timestamp: { microsSinceUnixEpoch: bigint }): number {
  return Number(timestamp.microsSinceUnixEpoch / 1000n);
}

/** Until browse views expose a formatted location, hide raw Google place ids. */
function formatBrowseLocation(googlePlaceId: string | undefined): string {
  if (!googlePlaceId) return "";
  if (googlePlaceId.startsWith("ChIJ")) return "";
  if (googlePlaceId.startsWith("seed-place-")) {
    return googlePlaceId
      .slice("seed-place-".length)
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return googlePlaceId;
}

function formatCanonicalAddress(address: CanonicalAddress | undefined): string {
  if (!address) return "";
  const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(" ");
  const locality = [address.city, address.state].filter(Boolean).join(", ");
  return [street, locality].filter(Boolean).join(", ");
}

function toCampaignColors(
  colorPrimary: string | undefined,
  colorText: string | undefined,
): CampaignColors | undefined {
  if (!colorPrimary) return undefined;
  return {
    primary: colorPrimary,
    secondary: colorPrimary,
    tertiary: colorPrimary,
    text: colorText ?? "#ffffff",
  };
}

function toNominatedBusiness(row: CampaignBrowseNomination): NominatedBusiness {
  const businessType = row.businessType as BusinessCategoryType | undefined;

  return {
    id: row.id,
    name: row.name ?? "",
    heroCdnPath: row.heroCdnPath,
    businessType,
    colors: toCampaignColors(row.colorPrimary, row.colorText),
  };
}

/**
 * Project a `my_business_card_interests` row onto the shared card-EOI shape used
 * by both the standalone sharable-card result and the folded-card case on a
 * claimed nomination. `bigint` amounts (SpacetimeDB `i64`) narrow to `number`.
 */
function toBusinessCardUserInterest(
  myInterest: MyBusinessCardInterest | undefined,
): BusinessCardUserInterest | undefined {
  if (!myInterest) return undefined;
  return {
    interestDollarAmount:
      typeof myInterest.interestDollarAmount === "bigint"
        ? Number(myInterest.interestDollarAmount)
        : myInterest.interestDollarAmount,
    firstName: myInterest.firstName ?? null,
    lastName: myInterest.lastName ?? null,
    supportiveMessage: myInterest.supportiveMessage ?? null,
    consentToDisplay: myInterest.consentToDisplay,
  };
}

/**
 * Stable ordering over two cards linked to the same nomination: older first,
 * ties broken on the card id. Both inputs are immutable, so the winner can't
 * change when the underlying view re-scans in a different order.
 */
function isOlderCard(
  candidate: CampaignBrowseShareableCard,
  incumbent: CampaignBrowseShareableCard,
): boolean {
  const candidateCreated = candidate.createdAt.microsSinceUnixEpoch;
  const incumbentCreated = incumbent.createdAt.microsSinceUnixEpoch;
  if (candidateCreated !== incumbentCreated) return candidateCreated < incumbentCreated;
  return candidate.cardId.toString() < incumbent.cardId.toString();
}

function mapNominationRow(
  row: CampaignBrowseNomination,
  sharableCardsByNominationId: ReadonlyMap<string, CampaignBrowseShareableCard>,
  myInterestsByBusinessId: ReadonlyMap<string, MyNominationInterest>,
  myInterestsByCardId: ReadonlyMap<string, MyBusinessCardInterest>,
): NominatedBrowseResult {
  const myInterest = myInterestsByBusinessId.get(row.id);
  // The card folded into this row, if any. We keep the whole row rather than a
  // boolean because the fold is what SWALLOWS the standalone card — and with it
  // the `/ttw/<cardId>` link the standalone card rendered. Without the id here,
  // a claimed business with a live card had no way to reach its own page.
  const linkedCard = sharableCardsByNominationId.get(row.id);
  const totalInterested = toNumber(row.totalInterested);
  const totalInterestDollars = toNumber(row.totalInterestDollars);

  return {
    type: row.isClaimed ? "claimedNominatedBusiness" : "unclaimedNominatedBusiness",
    location:
      formatCanonicalAddress(row.canonicalAddress) || formatBrowseLocation(row.googlePlaceId),
    businessTypeText: getFriendlyName(row.businessType as BusinessCategoryType | undefined),
    totalInterested,
    totalInterest: totalInterestDollars,
    createdAt: timestampToMs(row.createdAt),
    latitude: row.latitude ?? undefined,
    longitude: row.longitude ?? undefined,
    nominatedBusiness: toNominatedBusiness(row),
    nominatedByName: row.nominatedByDisplayName,
    hasSharableBusinessCard: linkedCard !== undefined || undefined,
    linkedCardId: linkedCard?.cardId.toString(),
    // `requiresAccessCode` is a non-optional bool on the view, so this narrows
    // `false` to `undefined` purely to match the `?: true` field type — unlike
    // the `|| undefined` on the standalone card path, it is not covering for
    // rows that might be missing the field.
    linkedCardRequiresAccessCode: linkedCard?.requiresAccessCode === true || undefined,
    linkedCardHasTtwPage: linkedCard?.hasTtwPage === true || undefined,
    linkedCardHasTtwPageTargetProperty: linkedCard?.hasTtwPageTargetProperty === true || undefined,
    linkedCardTargetLocation:
      linkedCard?.hasTtwPageTargetProperty === true
        ? formatCanonicalAddress(linkedCard.canonicalAddress) || undefined
        : undefined,
    linkedCardUserInterest: linkedCard
      ? toBusinessCardUserInterest(myInterestsByCardId.get(linkedCard.cardId.toString()))
      : undefined,
    userInterestDollars: myInterest?.intendedInvestmentDollars
      ? typeof myInterest.intendedInvestmentDollars === "bigint"
        ? Number(myInterest.intendedInvestmentDollars)
        : myInterest.intendedInvestmentDollars
      : undefined,
    userReason: myInterest?.reason ?? undefined,
    userFirstName: myInterest?.firstName ?? undefined,
    userLastName: myInterest?.lastName ?? undefined,
    userConsentToDisplay: myInterest?.consentToDisplay ?? undefined,
  };
}

function toSharableBusinessCard(card: CampaignBrowseShareableCard): SharableBusinessCard {
  const businessType = card.businessType as BusinessCategoryType | undefined;

  return {
    cardId: card.cardId.toString(),
    brandId: card.brandId.toString(),
    name: card.brandFriendlyName,
    heroCdnPath: card.heroCdnPath,
    businessType,
    colors: toCampaignColors(card.colorPrimary, card.colorText),
  };
}

function mapShareableCardRow(
  card: CampaignBrowseShareableCard,
  myInterestByCardId: ReadonlyMap<string, MyBusinessCardInterest>,
): SharableBusinessCardBrowseResult {
  const myInterest = myInterestByCardId.get(card.cardId.toString());
  return {
    type: "sharableBusinessCard",
    sharableBusinessCard: toSharableBusinessCard(card),
    location:
      formatCanonicalAddress(card.canonicalAddress) || formatBrowseLocation(card.googlePlaceId),
    businessTypeText: getFriendlyName(card.businessType as BusinessCategoryType | undefined),
    hasTtwPageTargetProperty: card.hasTtwPageTargetProperty,
    // `requiresAccessCode` is always set by the view, but the optional field
    // keeps older/synthetic test rows (where the bool hasn't been added) from
    // breaking the spread into `SharableBusinessCardBrowseResult`.
    requiresAccessCode: card.requiresAccessCode || undefined,
    hasTtwPage: card.hasTtwPage || undefined,
    totalInterested: toNumber(card.totalInterested),
    totalInterest: toNumber(card.totalInterestDollars),
    // The interest row is keyed by cardId (UUID), not by the underlying
    // business — passing the full row shape lets the browse card open the
    // edit dialog pre-filled with the user's existing first/last name,
    // supportive message, and consent flag without a second round-trip.
    userInterest: toBusinessCardUserInterest(myInterest),
    createdAt: timestampToMs(card.createdAt),
    latitude: card.latitude ?? undefined,
    longitude: card.longitude ?? undefined,
  };
}

/** Cents (SpacetimeDB `i64`) → whole dollars for display. */
function centsToDollars(cents: bigint | number): number {
  return Number(cents) / 100;
}

/**
 * A card whose page has been promoted to an open raise.
 *
 * Shares `location` / `businessTypeText` / `hasTtwPageTargetProperty` with the
 * planning-stage mapper, so a card looks the same either side of promotion
 * except for the stage treatment — which is the point of the unified design.
 *
 * `hasTtwPage` is NOT carried: an offering can only exist on a page (promotion
 * reads `ttw_pages_v2`), so a fundraising card always has somewhere to link.
 */
function mapOfferingCardRow(card: CampaignBrowseShareableCard): OfferingBrowseResult {
  return {
    type: "offering",
    sharableBusinessCard: toSharableBusinessCard(card),
    location:
      formatCanonicalAddress(card.canonicalAddress) || formatBrowseLocation(card.googlePlaceId),
    businessTypeText: getFriendlyName(card.businessType as BusinessCategoryType | undefined),
    hasTtwPageTargetProperty: card.hasTtwPageTargetProperty,
    offeringName: card.offeringName,
    targetAmount: centsToDollars(card.offeringTargetAmountCents),
    totalInvested: centsToDollars(card.offeringRaisedCents),
    totalInvestors: toNumber(card.offeringInvestorCount),
    endTimestamp: Number(card.offeringEndDateMs),
    // The EOI totals still ride along: a promoted card keeps its pledge history,
    // and the map/search surfaces read these regardless of stage.
    totalInterested: toNumber(card.totalInterested),
    totalInterest: toNumber(card.totalInterestDollars),
    createdAt: timestampToMs(card.createdAt),
    latitude: card.latitude ?? undefined,
    longitude: card.longitude ?? undefined,
  };
}

/** Display name for a result — its business/campaign title. */
export function getBrowseResultName(result: BrowseResult): string {
  if (result.type === "roadshow") return result.title;
  if (result.type === "offering" || result.type === "sharableBusinessCard") {
    return result.sharableBusinessCard.name;
  }
  return result.nominatedBusiness.name;
}

/**
 * Coarse classification driving the map pin color: live fundraising campaigns
 * (roadshows, offerings, published shareable cards) vs. community nominations.
 */
export function getBrowseResultKind(result: BrowseResult): "campaign" | "nomination" {
  return result.type === "claimedNominatedBusiness" || result.type === "unclaimedNominatedBusiness"
    ? "nomination"
    : "campaign";
}

export function getBrowseResultKey(result: BrowseResult): string {
  if (result.type === "roadshow") return result.campaign.campaignId;
  // Both card-backed stages key on the card, with distinct prefixes: a card that
  // is promoted mid-session changes result type, and a shared key would let
  // React reuse the planning card's DOM for a fundraising one.
  if (result.type === "offering") return `offering:${result.sharableBusinessCard.cardId}`;
  if (result.type === "sharableBusinessCard") {
    return `sharable:${result.sharableBusinessCard.cardId}`;
  }

  return result.nominatedBusiness.id;
}

/**
 * Assemble the browse list from the two projected views.
 *
 * Sharable cards whose brand traces back to a nomination claim carry
 * `linkedNominatedBusinessId` — those get folded into the corresponding
 * claimed-nomination row (the claimed-nomination card wins per product spec)
 * and the nomination is flagged as also-roadshow. Sharable cards without a
 * linked nomination become standalone results.
 *
 * A card with an OPEN offering is exempt from the fold and becomes its own
 * `fundraising` result — see the note at the fold itself.
 */
export function mapCampaignBrowseRows(
  nominations: CampaignBrowseNomination[],
  shareableCards: CampaignBrowseShareableCard[],
  myInterestsByBusinessId: ReadonlyMap<string, MyNominationInterest>,
  myInterestsByCardId: ReadonlyMap<string, MyBusinessCardInterest>,
): BrowseResult[] {
  const sharableCardsByNominationId = new Map<string, CampaignBrowseShareableCard>();
  const standaloneSharableCards: CampaignBrowseShareableCard[] = [];

  for (const card of shareableCards) {
    // ⚠️ A PROMOTED card is never folded into its nomination.
    //
    // The fold exists so a claimed business shows ONE row (the claimed-nomination
    // card wins per product spec) — but it drops the card's own result, and with
    // it any raise hanging off that card. Nomination → claim → card → promote is
    // the primary funnel, not an edge case, so folding a fundraising card away
    // would hide the raises this page exists to surface, and `/browse` would
    // still be advertising the business as "planning" while it takes money.
    //
    // A live raise outranks the nomination it grew out of, so the card survives
    // as its own `fundraising` row. The nomination row remains too, which is
    // correct: the community's original nomination is still a real, separately
    // backed thing, and it keeps its own pledge totals and "nominated by"
    // credit. The two rows are distinguishable — different stage, different
    // badge, different key.
    if (card.linkedNominatedBusinessId !== undefined && !card.hasOpenOffering) {
      // The fold renders ONE row, so if a nomination somehow has several cards
      // we must pick one — deterministically. Iteration order here is the view's
      // storage/index-scan order, which a mutating write (an EOI bumping
      // `totalInterested`) reshuffles; picking "whichever came first" would let
      // an unrelated pledge silently change this row's link target and Locked
      // badge between subscription pushes. Oldest card wins, tie-broken on id,
      // both immutable — the same reasoning as the `createdAt` browse sort.
      const existing = sharableCardsByNominationId.get(card.linkedNominatedBusinessId);
      if (existing === undefined || isOlderCard(card, existing)) {
        sharableCardsByNominationId.set(card.linkedNominatedBusinessId, card);
      }
    } else {
      // Includes promoted cards that DO carry a `linkedNominatedBusinessId` —
      // they skipped the fold above and stand on their own.
      standaloneSharableCards.push(card);
    }
  }

  const nominationResults = nominations.map((row) =>
    mapNominationRow(
      row,
      sharableCardsByNominationId,
      myInterestsByBusinessId,
      myInterestsByCardId,
    ),
  );
  // A promoted card becomes a `fundraising` result; everything else stays
  // `planning`.
  const sharableResults = standaloneSharableCards.map((card) =>
    card.hasOpenOffering
      ? mapOfferingCardRow(card)
      : mapShareableCardRow(card, myInterestsByCardId),
  );

  return [...nominationResults, ...sharableResults];
}
